import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import api from '../hooks/api';

const STATUSES = ['available', 'assigned', 'in_repair', 'retired', 'lost'];

const emptyServiceForm = { note: '', service_date: '', serviced_by: '', cost: '' };

const emptyForm = {
  asset_tag: '', name: '', type_id: '', brand: '', model: '', serial_no: '',
  ip_address: '', invoice_number: '', vendor_name: '',
  status: 'available', branch_id: '', department_id: '', employee_id: '',
  parent_id: '',
  purchase_date: '', purchase_cost: '', warranty_until: '', notes: ''
};

function StatusBadge({ status }) {
  return <span className={`badge badge-status badge-${status}`}>{status.replace('_', ' ')}</span>;
}

export default function DevicesPage() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [serviceDevice, setServiceDevice] = useState(null);
  const [serviceNotes, setServiceNotes] = useState([]);
  const [serviceForm, setServiceForm] = useState(emptyServiceForm);
  const [serviceError, setServiceError] = useState('');
  const [subParent, setSubParent] = useState(null);
  const [subDevices, setSubDevices] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAssignment, setFilterAssignment] = useState('');
  const [filterWarranty, setFilterWarranty] = useState('');
  const [filterPurchaseFrom, setFilterPurchaseFrom] = useState('');
  const [filterPurchaseTo, setFilterPurchaseTo] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterScope, setFilterScope] = useState('top');
  const [showMore, setShowMore] = useState(false);

  const loadDevices = useCallback(async () => {
    setDevices(await api.get('/api/devices?include_sub=true'));
  }, []);

  const loadMeta = useCallback(async () => {
    const [dt, b, d, e] = await Promise.all([
      api.get('/api/device-types'),
      api.get('/api/branches'),
      api.get('/api/departments'),
      api.get('/api/employees'),
    ]);
    setDeviceTypes(dt); setBranches(b); setDepartments(d); setEmployees(e);
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const filteredDepts = departments.filter(d => !form.branch_id || d.branch_id === form.branch_id);
  const filteredEmps = employees.filter(e =>
    (!form.branch_id || e.branch_id === form.branch_id) &&
    (!form.department_id || e.department_id === form.department_id)
  );

  async function openAdd() {
    setError('');
    try {
      const { asset_tag } = await api.get('/api/devices/next-tag');
      setForm({ ...emptyForm, asset_tag });
      setModal('add');
    } catch (err) { alert(err.message); }
  }

  function openEdit(d) {
    setForm({
      asset_tag: d.asset_tag, name: d.name, type_id: d.type_id, brand: d.brand || '',
      model: d.model || '', serial_no: d.serial_no || '',
      ip_address: d.ip_address || '', invoice_number: d.invoice_number || '', vendor_name: d.vendor_name || '',
      status: d.status, branch_id: d.branch_id, department_id: d.department_id || '',
      employee_id: d.employee_id || '', parent_id: d.parent_id || '',
      purchase_date: d.purchase_date || '',
      purchase_cost: d.purchase_cost ?? '', warranty_until: d.warranty_until || '',
      notes: d.notes || ''
    });
    setError(''); setModal({ mode: 'edit', id: d.id });
  }

  async function openSubDevices(device) {
    setSubParent(device);
    const children = await api.get('/api/devices?parent_id=' + device.id);
    setSubDevices(children);
  }

  async function reloadSubDevices() {
    if (!subParent) return;
    const children = await api.get('/api/devices?parent_id=' + subParent.id);
    setSubDevices(children);
  }

  async function openAddSub() {
    setError('');
    try {
      const { asset_tag } = await api.get('/api/devices/next-tag');
      setForm({
        ...emptyForm,
        asset_tag,
        parent_id: subParent.id,
        branch_id: subParent.branch_id,
        department_id: subParent.department_id || '',
        employee_id: subParent.employee_id || '',
        status: 'assigned',
      });
      setModal('add');
    } catch (err) { alert(err.message); }
  }

  async function handleDeleteSub(sub) {
    if (!window.confirm(`Delete sub-device ${sub.asset_tag} - ${sub.name}?`)) return;
    await api.delete('/api/devices/' + sub.id);
    reloadSubDevices();
    loadDevices();
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault(); setError('');
    try {
      const payload = { ...form, purchase_cost: form.purchase_cost ? parseFloat(form.purchase_cost) : null, employee_id: form.employee_id || null, department_id: form.department_id || null, parent_id: form.parent_id || null };
      if (modal === 'add') await api.post('/api/devices', payload);
      else await api.put('/api/devices/' + modal.id, payload);
      setModal(null); loadDevices();
      if (subParent) reloadSubDevices();
    } catch (err) { setError(err.message); }
  }

  async function openService(d) {
    setServiceDevice(d);
    setServiceForm(emptyServiceForm);
    setServiceError('');
    setServiceNotes(await api.get(`/api/devices/${d.id}/service-notes`));
  }

  async function handleAddServiceNote(e) {
    e.preventDefault(); setServiceError('');
    try {
      const payload = { ...serviceForm, cost: serviceForm.cost ? parseFloat(serviceForm.cost) : null };
      await api.post(`/api/devices/${serviceDevice.id}/service-notes`, payload);
      setServiceForm(emptyServiceForm);
      setServiceNotes(await api.get(`/api/devices/${serviceDevice.id}/service-notes`));
    } catch (err) { setServiceError(err.message); }
  }

  async function handleDeleteNote(noteId) {
    if (!window.confirm('Delete this service note?')) return;
    await api.delete(`/api/devices/${serviceDevice.id}/service-notes/${noteId}`);
    setServiceNotes(await api.get(`/api/devices/${serviceDevice.id}/service-notes`));
  }

  function handleExport() {
    const tagById = Object.fromEntries(devices.map(d => [d.id, d.asset_tag]));
    const columns = [
      ['Asset Tag', d => d.asset_tag],
      ['Name', d => d.name],
      ['Type', d => d.type_name],
      ['Brand', d => d.brand],
      ['Model', d => d.model],
      ['Serial No', d => d.serial_no],
      ['IP Address', d => d.ip_address],
      ['Invoice Number', d => d.invoice_number],
      ['Vendor Name', d => d.vendor_name],
      ['Status', d => d.status],
      ['Branch', d => d.branch_name],
      ['Department', d => d.department_name],
      ['Assigned To', d => d.employee_name],
      ['Emp Code', d => d.emp_code],
      ['Parent Tag', d => d.parent_id ? (tagById[d.parent_id] || '') : ''],
      ['Purchase Date', d => d.purchase_date],
      ['Purchase Cost', d => d.purchase_cost],
      ['Warranty Until', d => d.warranty_until],
      ['Notes', d => d.notes],
      ['Created At', d => d.created_at ? new Date(d.created_at).toISOString() : ''],
    ];
    const esc = v => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map(c => esc(c[0])).join(',');
    const body = visible.map(r => columns.map(c => esc(c[1](r))).join(',')).join('\n');
    const csv = '﻿' + header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `devices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  async function handleExportServiceNotes() {
    if (!serviceDevice) return;
    try { await api.download(`/api/devices/${serviceDevice.id}/service-notes/export`, `service-notes-${serviceDevice.asset_tag}.csv`); }
    catch (err) { alert(err.message); }
  }

  async function handleDelete(d) {
    if (!window.confirm(`Delete device ${d.asset_tag} - ${d.name}?`)) return;
    await api.delete('/api/devices/' + d.id);
    loadDevices();
  }

  const filterDeptOptions = departments.filter(d => !filterBranch || d.branch_id === filterBranch);
  const brandOptions = Array.from(new Set(devices.map(d => d.brand).filter(Boolean))).sort();
  const todayStr = new Date().toISOString().slice(0, 10);
  const in30Str = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const visible = devices.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      const ok = d.asset_tag.toLowerCase().includes(q) || d.name.toLowerCase().includes(q) ||
        (d.serial_no || '').toLowerCase().includes(q) || (d.employee_name || '').toLowerCase().includes(q) ||
        (d.ip_address || '').toLowerCase().includes(q);
      if (!ok) return false;
    }
    if (filterStatus && d.status !== filterStatus) return false;
    if (filterBranch && d.branch_id !== filterBranch) return false;
    if (filterDept && d.department_id !== filterDept) return false;
    if (filterType && d.type_id !== filterType) return false;
    if (filterAssignment === 'assigned' && !d.employee_id) return false;
    if (filterAssignment === 'unassigned' && d.employee_id) return false;
    if (filterBrand && d.brand !== filterBrand) return false;
    if (filterScope === 'top' && d.parent_id) return false;
    if (filterScope === 'sub' && !d.parent_id) return false;
    if (filterPurchaseFrom && (!d.purchase_date || d.purchase_date < filterPurchaseFrom)) return false;
    if (filterPurchaseTo && (!d.purchase_date || d.purchase_date > filterPurchaseTo)) return false;
    if (filterWarranty) {
      const w = d.warranty_until;
      if (filterWarranty === 'none' && w) return false;
      if (filterWarranty === 'valid' && (!w || w < todayStr)) return false;
      if (filterWarranty === 'expired' && (!w || w >= todayStr)) return false;
      if (filterWarranty === 'expiring' && (!w || w < todayStr || w > in30Str)) return false;
    }
    return true;
  });

  const activeFilterCount =
    (filterDept ? 1 : 0) + (filterType ? 1 : 0) + (filterAssignment ? 1 : 0) +
    (filterWarranty ? 1 : 0) + (filterPurchaseFrom ? 1 : 0) + (filterPurchaseTo ? 1 : 0) +
    (filterBrand ? 1 : 0) + (filterScope && filterScope !== 'top' ? 1 : 0);

  function clearFilters() {
    setSearch(''); setFilterStatus(''); setFilterBranch('');
    setFilterDept(''); setFilterType(''); setFilterAssignment('');
    setFilterWarranty(''); setFilterPurchaseFrom(''); setFilterPurchaseTo('');
    setFilterBrand(''); setFilterScope('top');
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Devices</h1>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={handleExport}>Export CSV</button>
          {(user?.role === 'admin' || user?.role === 'manager') &&
            <button className="btn btn-primary" onClick={openAdd}>+ Add Device</button>}
        </div>
      </div>
      <div className="filters">
        <input placeholder="Search asset tag, name, serial, IP, employee…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        {user?.role === 'admin' && (
          <select value={filterBranch} onChange={e => { setFilterBranch(e.target.value); setFilterDept(''); }}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {filterDeptOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {deviceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filterAssignment} onChange={e => setFilterAssignment(e.target.value)}>
          <option value="">All Assignments</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowMore(s => !s)}>
          {showMore ? 'Hide filters' : 'More filters'}{activeFilterCount > 0 && !showMore ? ` (${activeFilterCount})` : ''}
        </button>
        {(search || filterStatus || filterBranch || activeFilterCount > 0) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear</button>
        )}
      </div>
      {showMore && (
        <div className="filters" style={{ marginTop: 8 }}>
          <select value={filterWarranty} onChange={e => setFilterWarranty(e.target.value)}>
            <option value="">Any Warranty</option>
            <option value="valid">Under warranty</option>
            <option value="expiring">Expiring in 30 days</option>
            <option value="expired">Expired</option>
            <option value="none">No warranty set</option>
          </select>
          <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
            <option value="">All Brands</option>
            {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={filterScope} onChange={e => setFilterScope(e.target.value)}>
            <option value="">All Devices</option>
            <option value="top">Top-level only</option>
            <option value="sub">Sub-devices only</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            Purchased from
            <input type="date" value={filterPurchaseFrom} onChange={e => setFilterPurchaseFrom(e.target.value)} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            to
            <input type="date" value={filterPurchaseTo} onChange={e => setFilterPurchaseTo(e.target.value)} />
          </label>
        </div>
      )}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Asset Tag</th><th>Device</th><th>Type</th><th>Serial No.</th>
              <th>IP Address</th><th>Branch / Dept</th><th>Assigned To</th>
              <th>Status</th><th>Purchase Date</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(d => (
              <tr key={d.id}>
                <td className="mono">{d.asset_tag}</td>
                <td>
                  {d.name}
                  {d.brand ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {d.brand}</span> : ''}
                  {d.sub_device_count > 0 && <span className="count-badge" style={{ marginLeft: 8 }} title="Sub-devices">{d.sub_device_count}</span>}
                </td>
                <td><span className="tag">{d.type_name}</span></td>
                <td className="mono">{d.serial_no || '—'}</td>
                <td className="mono">{d.ip_address || '—'}</td>
                <td>{d.branch_name}{d.department_name ? ` / ${d.department_name}` : ''}</td>
                <td>{d.employee_name ? `${d.employee_name}${d.emp_code ? ` (${d.emp_code})` : ''}` : '—'}</td>
                <td><StatusBadge status={d.status} /></td>
                <td>{d.purchase_date || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(user?.role === 'admin' || user?.role === 'manager') &&
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(d)}>Edit</button>}
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => openSubDevices(d)}>Sub-devices</button>
                  {(user?.role === 'admin' || user?.role === 'manager') &&
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => openService(d)}>Service</button>}
                  {user?.role === 'admin' &&
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: 6 }} onClick={() => handleDelete(d)}>Delete</button>}
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={10} className="empty">No devices found</td></tr>}
          </tbody>
        </table>
      </div>

      {subParent && (
        <Modal title={`Sub-devices — ${subParent.asset_tag} · ${subParent.name}`} onClose={() => setSubParent(null)}>
          <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-muted)' }}>
            Accessories or components linked to this device (monitor, keyboard, mouse, etc.). They inherit branch and assignment from the parent by default.
          </div>
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }} onClick={openAddSub}>+ Add Sub-device</button>
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Asset Tag</th><th>Name</th><th>Type</th><th>Serial No.</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subDevices.map(s => (
                  <tr key={s.id}>
                    <td className="mono">{s.asset_tag}</td>
                    <td>{s.name}{s.brand ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {s.brand}</span> : ''}</td>
                    <td><span className="tag">{s.type_name}</span></td>
                    <td className="mono">{s.serial_no || '—'}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {(user?.role === 'admin' || user?.role === 'manager') &&
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>}
                      {user?.role === 'admin' &&
                        <button className="btn btn-danger btn-sm" style={{ marginLeft: 6 }} onClick={() => handleDeleteSub(s)}>Delete</button>}
                    </td>
                  </tr>
                ))}
                {subDevices.length === 0 && <tr><td colSpan={6} className="empty">No sub-devices yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {serviceDevice && (
        <Modal title={`Service History — ${serviceDevice.asset_tag} · ${serviceDevice.name}`} onClose={() => setServiceDevice(null)}>
          <form onSubmit={handleAddServiceNote} style={{ marginBottom: 20 }}>
            {serviceError && <div className="alert alert-error">{serviceError}</div>}
            <div className="form-group">
              <label>Service Note *</label>
              <textarea value={serviceForm.note} onChange={e => setServiceForm(f => ({ ...f, note: e.target.value }))} required placeholder="Describe the repair or maintenance work done…" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Service Date</label>
                <input type="date" value={serviceForm.service_date} onChange={e => setServiceForm(f => ({ ...f, service_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Serviced By</label>
                <input value={serviceForm.serviced_by} onChange={e => setServiceForm(f => ({ ...f, serviced_by: e.target.value }))} placeholder="Technician or vendor name" />
              </div>
            </div>
            <div className="form-group">
              <label>Cost (₹)</label>
              <input type="number" step="0.01" value={serviceForm.cost} onChange={e => setServiceForm(f => ({ ...f, cost: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Add Note</button>
            </div>
          </form>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                History ({serviceNotes.length})
              </div>
              {serviceNotes.length > 0 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleExportServiceNotes}>Export CSV</button>
              )}
            </div>
            {serviceNotes.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No service notes yet.</p>}
            <div className="timeline">
              {serviceNotes.map(n => (
                <div key={n.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>{n.note}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {n.service_date && <span>Date: {n.service_date}</span>}
                      {n.serviced_by && <span>By: {n.serviced_by}</span>}
                      {n.cost && <span>Cost: ₹{parseFloat(n.cost).toLocaleString()}</span>}
                      <span>Logged by {n.created_by_name} · {new Date(n.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteNote(n.id)} style={{ alignSelf: 'flex-start' }}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {modal && (
        <Modal title={modal === 'add' ? 'Add Device' : 'Edit Device'} onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label>Asset Tag {modal === 'add' ? '(auto-generated)' : ''}</label>
                <input value={form.asset_tag} disabled style={{ fontFamily: 'var(--mono)', background: 'var(--surface2)' }} />
              </div>
              <div className="form-group">
                <label>Device Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type *</label>
                <select value={form.type_id} onChange={e => set('type_id', e.target.value)} required>
                  <option value="">Select type…</option>
                  {deviceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select value={form.status} onChange={e => set('status', e.target.value)} required>
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Brand</label>
                <input value={form.brand} onChange={e => set('brand', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Model</label>
                <input value={form.model} onChange={e => set('model', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Serial No.</label>
                <input value={form.serial_no} onChange={e => set('serial_no', e.target.value)} />
              </div>
              <div className="form-group">
                <label>IP Address</label>
                <input value={form.ip_address} onChange={e => set('ip_address', e.target.value)} placeholder="192.168.1.1" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name</label>
                <input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Invoice Number</label>
                <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Branch *</label>
              <select value={form.branch_id} onChange={e => { set('branch_id', e.target.value); set('department_id', ''); set('employee_id', ''); }} required>
                <option value="">Select branch…</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Department *</label>
              <select value={form.department_id} onChange={e => { set('department_id', e.target.value); set('employee_id', ''); }} required disabled={!form.branch_id}>
                <option value="">{form.branch_id ? 'Select department…' : 'Select branch first'}</option>
                {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned Employee</label>
              <select value={form.employee_id} onChange={e => set('employee_id', e.target.value)} disabled={!form.department_id}>
                <option value="">{form.department_id ? 'Unassigned' : 'Select department first'}</option>
                {filteredEmps.map(e => <option key={e.id} value={e.id}>{e.name}{e.emp_code ? ` (${e.emp_code})` : ''}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Purchase Date</label>
                <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Purchase Cost (₹)</label>
                <input type="number" step="0.01" value={form.purchase_cost} onChange={e => set('purchase_cost', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Warranty Until</label>
              <input type="date" value={form.warranty_until} onChange={e => set('warranty_until', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary">{modal === 'add' ? 'Add Device' : 'Save Changes'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
